import { useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  EXAM_BOARDS,
  EXAM_GRADES,
  type ExamBoard,
  type ExamGrade,
  type ExamRegistration,
  type Page,
  type ReleaseResultsResponse,
  type ResultRecord,
  type SubjectResult,
  type VerifyQualificationResult,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/**
 * Exam registration + results-release-day workflow
 * (packages/modules/m28-education/src/routes/exams.ts). Registration and
 * fee collection are candidate self-service (SeyPay intent + webhook); this
 * console handles the EXAMS_OFFICER side: the officer registration queue,
 * batch results release (issues QR-verifiable DocVault certificates per
 * candidate), and certificate revocation/correction.
 */
export function Exams() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('EXAMS_OFFICER') ?? false;
  const isVerifier = isOfficer || (persona?.roles.includes('ANHRD_BOARD') ?? false);
  const [tab, setTab] = useState<'registrations' | 'results' | 'verify'>('registrations');

  return (
    <div>
      <PageHeader title="Exams" subtitle="IGCSE / A-Level / TVET / NATIONAL exam board registrations and results release." />
      <Tabs
        tabs={[
          { key: 'registrations', label: 'Registrations' },
          { key: 'results', label: 'Results & Certificates' },
          { key: 'verify', label: 'Verify Qualification' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {!isOfficer && tab !== 'verify' ? (
        <div className="mb-4">
          <ErrorNote message="Listing registrations and results, and releasing results, requires the Examinations Officer role." />
        </div>
      ) : null}
      {tab === 'registrations' ? <RegistrationsTab isOfficer={isOfficer} /> : tab === 'results' ? <ResultsTab isOfficer={isOfficer} /> : <VerifyTab isVerifier={isVerifier} />}
    </div>
  );
}

function RegistrationsTab({ isOfficer }: { isOfficer: boolean }) {
  const [board, setBoard] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (board) qs.set('board', board);
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<ExamRegistration>>(isOfficer ? `/api/v1/m28/exams/registrations?${qs.toString()}` : null);

  if (!isOfficer) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-52" placeholder="Search candidate NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-40" value={board} onChange={(e) => setBoard(e.target.value)}>
          <option value="">All boards</option>
          {EXAM_BOARDS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {(['REGISTERED', 'CONFIRMED', 'WITHDRAWN'] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No registrations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Candidate</th>
                <th className="table-head">Board</th>
                <th className="table-head">Subjects</th>
                <th className="table-head">Centre</th>
                <th className="table-head text-right">Fee</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(r.id)}>
                  <td className="table-cell font-mono text-xs">{r.candidateNin}</td>
                  <td className="table-cell">{r.board}</td>
                  <td className="table-cell text-xs text-slate-500">{r.subjects.join(', ')}</td>
                  <td className="table-cell">{r.centre}</td>
                  <td className="table-cell text-right tabular-nums">{scr(r.feeAmount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? <RegistrationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function RegistrationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<ExamRegistration>(`/api/v1/m28/exams/registrations/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reg = detailQ.data;

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m28/exams/registrations/${id}/withdraw`);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not withdraw registration');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Exam registration" onClose={onClose}>
      {detailQ.loading || !reg ? (
        <Spinner />
      ) : (
        <div className="space-y-3 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={reg.state} />
            <span className="font-mono text-xs text-slate-500">{reg.candidateNin}</span>
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Board</dt>
              <dd className="font-medium">{reg.board}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Subjects</dt>
              <dd className="font-medium">{reg.subjects.join(', ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Centre</dt>
              <dd className="font-medium">{reg.centre}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Fee</dt>
              <dd className="font-medium">{scr(reg.feeAmount)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Registered</dt>
              <dd className="font-medium">{formatDateTime(reg.registeredAt)}</dd>
            </div>
          </dl>
          {['REGISTERED', 'CONFIRMED'].includes(reg.state) ? (
            <div className="border-t border-slate-100 pt-3">
              <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={busy} onClick={withdraw}>
                Withdraw registration
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function ResultsTab({ isOfficer }: { isOfficer: boolean }) {
  const [board, setBoard] = useState('');
  const [q, setQ] = useState('');
  const [showRelease, setShowRelease] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (board) qs.set('board', board);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<ResultRecord>>(isOfficer ? `/api/v1/m28/exams/results?${qs.toString()}` : null);

  if (!isOfficer) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <input className="field-input !w-52" placeholder="Search candidate NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-40" value={board} onChange={(e) => setBoard(e.target.value)}>
            <option value="">All boards</option>
            {EXAM_BOARDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowRelease(true)}>
          + Release results (batch)
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No results released yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Candidate</th>
                <th className="table-head">Board</th>
                <th className="table-head">Cert serial</th>
                <th className="table-head">Released</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(r.id)}>
                  <td className="table-cell font-mono text-xs">{r.candidateNin}</td>
                  <td className="table-cell">{r.board}</td>
                  <td className="table-cell font-mono text-xs">{r.certSerial}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(r.releasedAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.revokedAt ? 'REVOKED' : 'ISSUED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRelease ? (
        <ReleaseResultsModal
          onClose={() => setShowRelease(false)}
          onDone={() => {
            setShowRelease(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? <ResultDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

interface DraftCandidate {
  candidateNin: string;
  subjectResults: SubjectResult[];
}

function ReleaseResultsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [board, setBoard] = useState<ExamBoard>('NATIONAL');
  const [candidates, setCandidates] = useState<DraftCandidate[]>([{ candidateNin: '', subjectResults: [{ subjectCode: '', grade: 'A' }] }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<ReleaseResultsResponse | null>(null);

  const updateCandidate = (idx: number, nin: string) => {
    setCandidates((cs) => cs.map((c, i) => (i === idx ? { ...c, candidateNin: nin } : c)));
  };
  const updateSubject = (cIdx: number, sIdx: number, patch: Partial<SubjectResult>) => {
    setCandidates((cs) =>
      cs.map((c, i) => (i === cIdx ? { ...c, subjectResults: c.subjectResults.map((s, j) => (j === sIdx ? { ...s, ...patch } : s)) } : c)),
    );
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const results = candidates
        .filter((c) => c.candidateNin.trim().length > 0)
        .map((c) => ({ candidateNin: c.candidateNin.trim(), subjectResults: c.subjectResults.filter((s) => s.subjectCode.trim().length > 0) }));
      const res = await api.post<ReleaseResultsResponse>('/api/v1/m28/exams/results/release', { board, results });
      setSummary(res);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to release results');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Release results (results-release-day batch)" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {summary ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            Released {summary.released.length} certificate(s); skipped {summary.skipped.length}.
            {summary.skipped.length > 0 ? (
              <ul className="mt-1 list-disc pl-4">
                {summary.skipped.map((s, i) => (
                  <li key={i}>
                    {s.candidateNin}: {s.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        <Field label="Board">
          <select className="field-input !w-40" value={board} onChange={(e) => setBoard(e.target.value as ExamBoard)}>
            {EXAM_BOARDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </Field>
        <div className="max-h-80 space-y-3 overflow-y-auto">
          {candidates.map((c, cIdx) => (
            <div key={cIdx} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center gap-2">
                <input
                  className="field-input flex-1"
                  placeholder="Candidate NIN"
                  value={c.candidateNin}
                  onChange={(e) => updateCandidate(cIdx, e.target.value)}
                />
                {candidates.length > 1 ? (
                  <button className="text-xs text-rose-500" onClick={() => setCandidates((cs) => cs.filter((_, i) => i !== cIdx))}>
                    Remove
                  </button>
                ) : null}
              </div>
              <div className="space-y-1.5">
                {c.subjectResults.map((s, sIdx) => (
                  <div key={sIdx} className="flex items-center gap-2">
                    <input
                      className="field-input flex-1 !py-1 text-xs"
                      placeholder="Subject code"
                      value={s.subjectCode}
                      onChange={(e) => updateSubject(cIdx, sIdx, { subjectCode: e.target.value })}
                    />
                    <select
                      className="field-input !w-20 !py-1 text-xs"
                      value={s.grade}
                      onChange={(e) => updateSubject(cIdx, sIdx, { grade: e.target.value as ExamGrade })}
                    >
                      {EXAM_GRADES.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                    {c.subjectResults.length > 1 ? (
                      <button
                        className="text-xs text-rose-500"
                        onClick={() =>
                          setCandidates((cs) => cs.map((cc, i) => (i === cIdx ? { ...cc, subjectResults: cc.subjectResults.filter((_, j) => j !== sIdx) } : cc)))
                        }
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                ))}
                <button
                  className="btn-secondary !px-2 !py-0.5 text-[11px]"
                  onClick={() =>
                    setCandidates((cs) => cs.map((cc, i) => (i === cIdx ? { ...cc, subjectResults: [...cc.subjectResults, { subjectCode: '', grade: 'A' }] } : cc)))
                  }
                >
                  + Subject
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          className="btn-secondary !px-2.5 !py-1 text-xs"
          onClick={() => setCandidates((cs) => [...cs, { candidateNin: '', subjectResults: [{ subjectCode: '', grade: 'A' }] }])}
        >
          + Add candidate
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || candidates.every((c) => !c.candidateNin.trim())}>
            {busy ? 'Releasing…' : 'Release results'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResultDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<ResultRecord>(`/api/v1/m28/exams/results/${id}`);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const result = detailQ.data;

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m28/exams/results/${id}/revoke`, { reason });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not revoke result');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Exam result / certificate" onClose={onClose}>
      {detailQ.loading || !result ? (
        <Spinner />
      ) : (
        <div className="space-y-3 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={result.revokedAt ? 'REVOKED' : 'ISSUED'} />
            <span className="font-mono text-xs text-slate-500">{result.candidateNin}</span>
          </div>
          <p className="text-xs text-slate-500">
            {result.board} · certificate <span className="font-mono">{result.certSerial}</span> · issued {formatDateTime(result.certIssuedAt)}
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="table-head">Subject</th>
                <th className="table-head">Grade</th>
              </tr>
            </thead>
            <tbody>
              {result.subjectResults.map((s, i) => (
                <tr key={i}>
                  <td className="table-cell">{s.subjectCode}</td>
                  <td className="table-cell font-semibold">{s.grade}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.revokedAt ? (
            <p className="text-xs text-rose-600">Revoked {formatDateTime(result.revokedAt)}: {result.revokeReason}</p>
          ) : (
            <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
              <Field label="Revoke reason" className="!mb-0 flex-1">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Correction reason…" />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={revoke}>
                Revoke
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function VerifyTab({ isVerifier }: { isVerifier: boolean }) {
  const [nin, setNin] = useState('');
  const [result, setResult] = useState<VerifyQualificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.get<VerifyQualificationResult>(`/api/v1/m28/verify/qualification/${encodeURIComponent(nin)}`);
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">edu:verify-qualification</h2>
      <p className="mb-4 text-xs text-slate-500">
        {isVerifier
          ? 'Staff lookup — allowed outright for Examinations Officer / ANHRD Board.'
          : 'Any other caller requires an affirmative C7 ConsentHub check for this candidate first.'}
      </p>
      {error ? <ErrorNote message={error} /> : null}
      <div className="flex items-end gap-2">
        <Field label="Candidate NIN" className="!mb-0 flex-1">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. SC160304012" />
        </Field>
        <button className="btn-primary" disabled={busy || nin.trim().length < 5} onClick={check}>
          {busy ? 'Checking…' : 'Check'}
        </button>
      </div>
      {result ? (
        <div className="mt-4 border-t border-slate-100 pt-3">
          {!result.found ? (
            <p className="text-sm text-slate-500">No released exam results on file for this NIN.</p>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {result.qualifications.map((q, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-2.5 py-1.5">
                  <span>
                    {q.board} — {q.subjectResults.map((s) => `${s.subjectCode}:${s.grade}`).join(', ')}
                  </span>
                  <StatusBadge status={q.certValid ? 'VALID' : 'REVOKED'} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
